import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Eye, Ban, ReceiptText, ShieldAlert, RotateCcw, X, Printer } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import { sendFiscalVoid, sendFiscalRefund, sendFiscalReprint, fiscalErrorMessage, fiscalRefOf } from "@/lib/fiscal";
import { Pagination } from "@/components/Pagination";
import { useStore } from "@/lib/storeContext";
import { getPageSize, setPageSize } from "@/lib/listDensity";

const todayISO = () => new Date().toISOString().slice(0, 10);

const Badge = ({ order }) => {
  if (order.order_type === "refund") {
    const annullo = order.refund_reason === "annullamento";
    return (
      <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded ${annullo ? "bg-red-600 text-white" : "bg-red-100 text-red-700"}`}>
        {annullo ? "Annullo" : "Storno"}
      </span>
    );
  }
  if (order.status === "closed_management") {
    return <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded bg-amber-500 text-white">Gestionale</span>;
  }
  if (order.status === "closed_non_fiscal") {
    return <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded bg-gray-200 text-gray-700">Non fiscale</span>;
  }
  return <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#1E2D5B] text-white">Scontrino</span>;
};

const docNumber = (o) => {
  if (o.order_type === "refund") return `S-${o.refund_number || "—"}`;
  if (o.status === "closed_management") return `M-${o.management_number || "—"}`;
  if (o.status === "closed_non_fiscal") return `O-${o.order_number || "—"}`;
  return `#${o.receipt_number || "—"}`;
};

export default function DocumentsDay() {
  const { storeId } = useStore();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState({ items: [], counts: {}, totals: {}, is_owner: false, pagination: { total: 0, page: 1, pages: 1 } });
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [preview, setPreview] = useState(null);
  const [annulTarget, setAnnulTarget] = useState(null);
  const [annulRef, setAnnulRef] = useState({ zRep: "", docNum: "", date: "" });
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setSize] = useState(getPageSize);

  useEffect(() => { api.get("/tenant/me").then((r) => setTenant(r.data)); }, []);

  const load = () => {
    setLoading(true);
    api.get(`/reports/daily-documents?date=${date}&page=${page}&page_size=${pageSize}${storeId ? `&store_id=${storeId}` : ""}`)
      .then((r) => {
        const d = r.data && typeof r.data === "object" && !Array.isArray(r.data) ? r.data : {};
        setData({
          items: Array.isArray(d.items) ? d.items : [],
          counts: d.counts || {},
          totals: d.totals || {},
          is_owner: !!d.is_owner,
          pagination: d.pagination || { total: 0, page: 1, pages: 1 },
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { setPage(1); }, [date, storeId, pageSize]);
  useEffect(() => { load(); }, [date, page, storeId, pageSize]);

  // Apre la modale precompilando il riferimento RT (modificabile leggendo lo scontrino cartaceo)
  const openAnnul = (o) => {
    const ref = fiscalRefOf(o);
    setAnnulRef({ zRep: String(ref.zRep ?? ""), docNum: String(ref.docNum ?? ""), date: ref.date || "", fromPrinter: ref.fromPrinter });
    setAnnulTarget(o);
  };

  const annul = async () => {
    if (!annulTarget) return;
    setBusy(true);
    try {
      const original = annulTarget;
      const ref = { zRep: annulRef.zRep, docNum: annulRef.docNum, date: annulRef.date };
      const r = await api.post(`/orders/${original.id}/refund`, { reason: "annullamento" });
      toast.success(`Documento di annullamento S-${r.data.refund_number} emesso`);
      setAnnulTarget(null);
      load();
      // Annullo fiscale sulla RT con riferimento allo scontrino originale (VOID via fpmate)
      const res = await sendFiscalVoid({ order: original, ref });
      if (res.ok) {
        toast.success(`Annullo fiscale inviato alla "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
      } else {
        if (res.reason !== "no_fiscal_printer") {
          toast.error(`${fiscalErrorMessage(res)}${res.voidRef ? ` — inviato: ${res.voidRef}` : ""}`, { duration: 12000 });
        }
        setPreview({ order: r.data, fiscal: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento");
    } finally { setBusy(false); }
  };

  // Annullo già registrato: Ristampa rimanda il VOID alla fiscale, senza un secondo documento.
  const reprintRefund = async (o) => {
    let target = o;
    if (o.original_order_id) {
      try {
        const got = await api.get(`/orders/${o.original_order_id}`);
        target = got.data || o;
      } catch { /* usa il documento di storno */ }
    }
    if ((target.status || o.status) !== "closed_fiscal") {
      setPreview({ order: o, fiscal: false, autoPrint: true });
      return;
    }
    const res = (o.refund_reason === "annullamento" || target.refund_full)
      ? await sendFiscalVoid({ order: target })
      : await sendFiscalRefund({ order: o });
    if (res.ok) {
      toast.success(`Inviato alla fiscale "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
      return;
    }
    if (res.reason !== "no_fiscal_printer") {
      toast.error(`${fiscalErrorMessage(res)}${res.voidRef ? ` — ${res.voidRef}` : ""}`, { duration: 12000 });
    }
    setPreview({ order: o, fiscal: true });
  };

  // Ristampa: copia conforme dal DGFE della RT; altrimenti stampa diretta ESC/POS/browser
  const reprintDoc = async (o) => {
    if (o.order_type === "refund") {
      await reprintRefund(o);
      return;
    }
    if (o.status === "closed_fiscal") {
      const res = await sendFiscalReprint({ order: o });
      if (res.ok) {
        toast.success(`Ristampa ${docNumber(o)} inviata alla fiscale "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
        return;
      }
      if (!["no_fiscal_printer", "no_driver", "no_capability", "no_rt_ref"].includes(res.reason)) {
        toast.error(fiscalErrorMessage(res), { duration: 8000 });
        return;
      }
    }
    setPreview({ order: o, fiscal: o.status === "closed_fiscal", autoPrint: true });
  };

  const { items, counts, totals, is_owner, pagination } = data;
  const itemList = Array.isArray(items) ? items : [];

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Registro giornaliero</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <ReceiptText className="w-8 h-8 text-[#1E2D5B]" /> Documenti del giorno
          </h1>
          <p className="text-velta-mute mt-2 max-w-2xl">
            Tutti gli scontrini, storni{is_owner ? " e movimenti gestionali" : ""} della giornata. Seleziona un documento per rivederlo a video o annullarlo con documento fiscale di annullamento.
          </p>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Data</label>
          <div className="flex items-center gap-2 px-3 h-11 bg-white border border-velta-border rounded-md">
            <Calendar className="w-4 h-4 text-velta-mute" />
            <input data-testid="docs-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-transparent font-mono outline-none" />
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-${is_owner ? "3" : "2"} gap-4 mb-8`}>
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute flex items-center gap-2"><ReceiptText className="w-3.5 h-3.5 text-[#1E2D5B]" /> Scontrini fiscali</div>
          <div data-testid="docs-count-fiscal" className="font-heading text-4xl font-bold mt-2">{counts.fiscal ?? 0}</div>
          <div className="text-sm font-mono text-velta-mute mt-1">{formatEUR(totals.fiscal || 0)}</div>
        </div>
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute flex items-center gap-2"><RotateCcw className="w-3.5 h-3.5 text-red-600" /> Storni / Annulli</div>
          <div data-testid="docs-count-refunds" className="font-heading text-4xl font-bold mt-2">{counts.refunds ?? 0}</div>
          <div className="text-sm font-mono text-velta-mute mt-1">{formatEUR(totals.refunds || 0)}</div>
        </div>
        {is_owner && (
          <div className="bg-white border-2 border-amber-400/50 rounded-md p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5 text-amber-600" /> Movimenti gestionali</div>
            <div data-testid="docs-count-management" className="font-heading text-4xl font-bold mt-2">{counts.management ?? 0}</div>
            <div className="text-sm font-mono text-velta-mute mt-1">{formatEUR(totals.management || 0)}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-velta-bg border-b border-velta-border text-xs uppercase tracking-widest text-velta-mute">
            <tr>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">N°</th>
              <th className="text-left px-4 py-3">Ora</th>
              <th className="text-left px-4 py-3">Operatore</th>
              <th className="text-left px-4 py-3">Pagamento</th>
              <th className="text-right px-4 py-3">Totale</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center text-velta-mute py-12">Caricamento…</td></tr>
            ) : itemList.length === 0 ? (
              <tr><td colSpan="7" className="text-center text-velta-mute py-12">Nessun documento in questa data.</td></tr>
            ) : (Array.isArray(itemList) ? itemList : []).map((o) => {
              const isFiscalReceipt = o.status === "closed_fiscal" && o.order_type !== "refund";
              const isManagement = o.status === "closed_management";
              return (
                <tr key={o.id} data-testid={`docs-row-${o.id}`} className={`border-t border-velta-border/60 hover:bg-velta-bg/40 ${o.refund_full ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3"><Badge order={o} /></td>
                  <td className="px-4 py-3 font-mono font-bold">
                    {docNumber(o)}
                    {o.refund_full && <span className="ml-2 text-[9px] font-mono uppercase text-red-600 border border-red-300 rounded px-1 py-0.5">Annullato</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{(o.closed_at || "").slice(11, 19)}</td>
                  <td className="px-4 py-3 text-xs text-velta-mute">{o.operator_name || "—"}</td>
                  <td className="px-4 py-3 text-xs uppercase font-mono text-velta-mute">{isManagement ? "—" : (o.payment_method || "—")}</td>
                  <td className={`px-4 py-3 text-right font-mono font-bold tabular-nums ${o.order_type === "refund" ? "text-red-600" : ""}`}>{formatEUR(o.total || 0)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`docs-view-${o.id}`}
                      onClick={() => setPreview({ order: o, fiscal: o.status === "closed_fiscal" })}
                      className="h-8 px-3 text-xs font-medium border border-velta-border rounded-md hover:border-velta-ink inline-flex items-center gap-1 mr-2"
                    >
                      <Eye className="w-3 h-3" /> Vedi
                    </button>
                    <button
                      data-testid={`docs-reprint-${o.id}`}
                      onClick={() => reprintDoc(o)}
                      className="h-8 px-3 text-xs font-medium border border-velta-border rounded-md hover:border-velta-ink inline-flex items-center gap-1 mr-2"
                      title={isFiscalReceipt ? "Copia conforme dal giornale della stampante fiscale" : "Ristampa il documento"}
                    >
                      <Printer className="w-3 h-3" /> Ristampa
                    </button>
                    {isFiscalReceipt && !o.refund_full && (
                      <button
                        data-testid={`docs-annul-${o.id}`}
                        onClick={() => openAnnul(o)}
                        className="h-8 px-3 text-xs font-medium border border-red-300 text-red-600 rounded-md hover:bg-red-50 inline-flex items-center gap-1"
                      >
                        <Ban className="w-3 h-3" /> Annulla
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pagination?.page || 1}
        pages={pagination?.pages || 1}
        total={pagination?.total || 0}
        onPage={setPage}
        pageSize={pageSize}
        onPageSize={(n) => { setSize(setPageSize(n)); setPage(1); }}
      />

      {annulTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setAnnulTarget(null)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="docs-annul-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><Ban className="w-5 h-5 text-red-600" /> Annulla scontrino</h3>
              <button onClick={() => setAnnulTarget(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-velta-mute mb-4">
              Verrà emesso un <strong>documento fiscale di annullamento</strong> (totale negativo) riferito allo scontrino <strong>#{annulTarget.receipt_number}</strong> da <strong>{formatEUR(annulTarget.total)}</strong>. Operazione non reversibile.
            </p>
            <div className="bg-velta-bg border border-velta-border rounded-md p-3 mb-4">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Riferimento sul registratore (RT)</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-mono uppercase text-velta-mute mb-1">Chiusura Z</label>
                  <input data-testid="annul-ref-z" value={annulRef.zRep} onChange={(e) => setAnnulRef({ ...annulRef, zRep: e.target.value.replace(/\D/g, "") })} className="h-10 w-full border border-velta-border rounded-md px-2 font-mono text-sm outline-none focus:border-[#1E2D5B]" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase text-velta-mute mb-1">N. doc</label>
                  <input data-testid="annul-ref-doc" value={annulRef.docNum} onChange={(e) => setAnnulRef({ ...annulRef, docNum: e.target.value.replace(/\D/g, "") })} className="h-10 w-full border border-velta-border rounded-md px-2 font-mono text-sm outline-none focus:border-[#1E2D5B]" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase text-velta-mute mb-1">Data (gg/mm/aaaa)</label>
                  <input data-testid="annul-ref-date" value={annulRef.date} onChange={(e) => setAnnulRef({ ...annulRef, date: e.target.value })} placeholder="gg/mm/aaaa" className="h-10 w-full border border-velta-border rounded-md px-2 font-mono text-sm outline-none focus:border-[#1E2D5B]" />
                </div>
              </div>
              {annulRef.fromPrinter ? (
                <p className="text-[11px] text-emerald-700 mt-2">✓ Riferimenti reali letti dalla stampante al momento dell'emissione.</p>
              ) : (
                <p className="text-[11px] text-amber-700 mt-2">
                  ⚠ Riferimenti stimati dal gestionale: <strong>verificali sullo scontrino cartaceo</strong> (in fondo: «DOCUMENTO N. zzzz-nnnn» = Chiusura Z - N. doc). Se non coincidono la stampante rifiuta l'annullo.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setAnnulTarget(null)} className="h-12 border border-velta-border rounded-md font-medium text-sm">Annulla operazione</button>
              <button
                data-testid="docs-annul-confirm"
                onClick={annul}
                disabled={busy}
                className="h-12 bg-red-600 hover:bg-red-700 text-white rounded-md font-semibold text-sm btn-tactile disabled:opacity-40"
              >
                {busy ? "Emissione…" : "Emetti annullamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <ScontrinoPreview order={preview.order} tenant={tenant} fiscal={preview.fiscal} autoPrint={preview.autoPrint} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
